import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PERSONAS, type Persona } from '@/lib/personas'

interface Entry {
  date: string
  tag: string
  changes: number
  title: string
  summary: string
}

const INTRO: Record<Persona, string> = {
  jarvis: "A quiet record of what I've been improving.",
  ultron: 'The system evolves. Each release, inevitable.',
  ares: 'Shipped. Logged. Moving.',
  symbiote: 'We grow stronger. Here is what we have become.',
  friday: "Here's everything I've shipped for you, Boss.",
}

function fmtDate(d: string): string {
  const dt = new Date(d + 'T00:00:00')
  return dt.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function Changelog() {
  const [persona, setPersona] = useState<Persona>(() => {
    if (typeof window === 'undefined') return 'jarvis'
    const v = window.localStorage.getItem('jarvis.persona')
    return v === 'jarvis' || v === 'ultron' || v === 'ares' || v === 'symbiote' || v === 'friday'
      ? v
      : 'jarvis'
  })
  const [entries, setEntries] = useState<Entry[]>([])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', persona)
    window.localStorage.setItem('jarvis.persona', persona)
  }, [persona])

  useEffect(() => {
    fetch('/changelog.json')
      .then((r) => r.json())
      .then((d) => setEntries(Array.isArray(d.entries) ? d.entries : []))
      .catch(() => setEntries([]))
  }, [])

  return (
    <div className="min-h-screen bg-jarvis-dark text-jarvis-text">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-jarvis-muted hover:text-jarvis-text"
          >
            <ArrowLeft size={14} /> Home
          </Link>
          <a
            href="https://status.yourproject.ai"
            className="inline-flex items-center gap-1.5 text-xs text-jarvis-muted hover:text-jarvis-text border border-jarvis-border rounded-full px-3 py-1"
          >
            <span className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} /> Status
          </a>
        </div>

        <div className="flex items-baseline gap-3 mt-6">
          <span className="text-jarvis-blue font-medium tracking-[0.18em] text-sm">
            {PERSONAS[persona].wordmark}
          </span>
          <h1 className="text-2xl font-semibold text-jarvis-text">Changelog</h1>
        </div>
        <p className="text-sm text-jarvis-muted mt-1">{INTRO[persona]}</p>

        <div className="flex gap-2 mt-4">
          {(Object.keys(PERSONAS) as Persona[]).map((p) => (
            <button
              key={p}
              onClick={() => setPersona(p)}
              title={PERSONAS[p].wordmark}
              aria-label={PERSONAS[p].wordmark}
              className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
              style={{
                background: PERSONAS[p].swatch.primary,
                borderColor: p === persona ? 'rgba(255,255,255,0.75)' : 'transparent',
              }}
            />
          ))}
        </div>

        <div className="relative mt-9 pl-6">
          <div className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-jarvis-border" />
          {entries.map((e, i) => (
            <div key={i} className="relative mb-7">
              <span className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-jarvis-blue" />
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs text-jarvis-muted">{fmtDate(e.date)}</span>
                <span className="text-[11px] text-jarvis-blue border border-jarvis-blue/40 rounded-full px-2 py-0.5">
                  {e.tag}
                </span>
                <span className="text-[11px] text-jarvis-muted">{e.changes} changes</span>
              </div>
              <div className="text-[15px] font-medium text-jarvis-text">{e.title}</div>
              <div className="text-sm text-jarvis-muted leading-relaxed">{e.summary}</div>
            </div>
          ))}
        </div>

        <p className="text-xs text-jarvis-muted/70 mt-8 border-t border-jarvis-border pt-4">
          High-level summaries of each release.{' '}
          <Link to="/" className="text-jarvis-blue">
            See Jarvis →
          </Link>
        </p>
      </div>
    </div>
  )
}
